import React from 'react';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { useSetAtom } from 'jotai';
import { PackageCompleteness } from '../../../types/packager-types';
import {
  completenessModalPackageIdAtom,
  packageEntriesDrilldownAtom,
} from '../../../atoms/packager-atoms';

interface ICompletenessFunnelProps {
  completeness: PackageCompleteness;
  packageId: string;
}

// Plain-language helper copy for technical-sounding causes (owned by us, not the
// API). Keyed by the fixed gap `cause` values.
const CAUSE_HELP: Record<string, string> = {
  no_extraction: "We couldn't extract any data from these documents.",
  empty_result: 'Extraction ran but produced no rows for these documents.',
  render_error: "Something failed while building these documents' rows.",
};

// The plain two-step funnel: "kept for delivery" → "delivered", with the single
// (always-bad) drop in between. Intentionally-excluded documents never enter this
// funnel, so the counts can't be skewed by routine duplicates.
const CompletenessFunnel: React.FC<ICompletenessFunnelProps> = ({ completeness, packageId }) => {
  const theme = useTheme();
  const setDrilldown = useSetAtom(packageEntriesDrilldownAtom);
  const setCompletenessModalPackageId = useSetAtom(completenessModalPackageIdAtom);

  // Hide count-0 causes — the gap is a fixed 3-cause set, often all zero.
  const drops = completeness.gap.filter((cause) => cause.count > 0);

  const handleViewDrilldown = (label: string, params: Record<string, string>) => {
    // Swap, don't stack: open the entries modal and close this one.
    setDrilldown({ packageId, params, label });
    setCompletenessModalPackageId(null);
  };

  return (
    <Box sx={{ my: 2 }}>
      <Typography sx={{ fontSize: '13px', fontWeight: 600, color: theme.palette.custom.darkBlue }}>
        {completeness.documents_to_deliver.toLocaleString()} documents kept for delivery
      </Typography>

      <Box sx={{ pl: 2, borderLeft: `2px solid ${theme.palette.divider}`, my: 1 }}>
        {drops.length === 0 ? (
          <Typography sx={{ fontSize: '12px', color: theme.palette.success.main, py: 0.5 }}>
            Nothing was lost along the way.
          </Typography>
        ) : (
          drops.map((cause) => (
            <Box
              key={cause.cause}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 2,
                py: 0.75,
              }}
              data-cy={`completeness-gap-${cause.cause}`}
            >
              <Box>
                <Typography
                  sx={{ fontSize: '13px', fontWeight: 600, color: theme.palette.error.main }}
                >
                  {cause.count.toLocaleString()} {cause.label}
                </Typography>
                {CAUSE_HELP[cause.cause] && (
                  <Typography sx={{ fontSize: '11px', color: theme.palette.text.secondary }}>
                    {CAUSE_HELP[cause.cause]}
                  </Typography>
                )}
              </Box>
              {cause.drilldown && (
                <Button
                  size="small"
                  color="error"
                  onClick={() => handleViewDrilldown(cause.label, cause.drilldown!)}
                  data-cy={`btn-completeness-view-${cause.cause}`}
                  sx={{ flexShrink: 0, textTransform: 'none' }}
                >
                  View →
                </Button>
              )}
            </Box>
          ))
        )}
      </Box>

      <Typography
        sx={{
          fontSize: '13px',
          fontWeight: 600,
          color: completeness.is_complete ? theme.palette.success.main : theme.palette.error.main,
        }}
      >
        {completeness.documents_delivered.toLocaleString()} documents delivered
      </Typography>

      <Typography sx={{ fontSize: '11px', color: theme.palette.text.secondary, mt: 1.5 }}>
        Documents set aside on purpose (e.g. duplicates) are listed in the CoC report.
      </Typography>
    </Box>
  );
};

export default CompletenessFunnel;
